<template>
  <div>
    <div class="pm-toolbar pm-toolbar--wrap">
      <div class="pm-seg" v-if="families.length > 1">
        <div :class="{'on': !family}" @click="setFamily('')" qid="pm-family-all">
          {{ $t('projects.management.all') }}<span class="pm-seg__n" v-if="counts.all !== undefined">{{ counts.all }}</span>
        </div>
        <div v-for="item in families"
             :key="item.key"
             :class="{'on': family === item.key}"
             @click="setFamily(item.key)"
             :qid="'pm-family-' + item.key">
          {{ $t('projects.management.families.' + item.key) }}<span class="pm-seg__n" v-if="counts[item.key] !== undefined">{{ counts[item.key] }}</span>
        </div>
      </div>

      <label class="pm-chip" :class="{'pm-chip--set': status !== 'all'}">
        <select v-model="status" @change="reload" qid="pm-filter-status">
          <option value="open">{{ $t('projects.management.filters.status_open') }}</option>
          <option value="done">{{ $t('projects.management.filters.status_done') }}</option>
          <option value="all">{{ $t('projects.management.filters.status_all') }}</option>
        </select>
        <font-awesome-icon icon="chevron-down"/>
      </label>
      <label class="pm-chip" :class="{'pm-chip--set': schedule}">
        <select v-model="schedule" @change="reload" qid="pm-filter-schedule">
          <option value="">{{ $t('projects.management.filters.schedule_all') }}</option>
          <option value="continuous">{{ $t('projects.management.recurring') }}</option>
          <option value="one time">{{ $t('projects.management.one_time') }}</option>
        </select>
        <font-awesome-icon icon="chevron-down"/>
      </label>
      <label class="pm-chip" :class="{'pm-chip--set': scope}" v-if="scopeOptions.length">
        <select v-model="scope" @change="reload" qid="pm-filter-scope">
          <option value="">{{ $t('projects.management.filters.scope_all') }}</option>
          <option v-for="option in scopeOptions" :key="option" :value="option">{{ $t('projects.launcher.scopes.' + option) }}</option>
        </select>
        <font-awesome-icon icon="chevron-down"/>
      </label>
      <div class="pm-chip pm-chip--multi" :class="{'pm-chip--set': owners.length}">
        <multiselect
            v-model="owners"
            :options="users"
            :multiple="true"
            track-by="id"
            label="name"
            :placeholder="$t('projects.management.filters.contributor')"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            @search-change="loadUsers"
            @input="reload"
            qid="pm-filter-contributor"
        ></multiselect>
      </div>
      <div class="pm-chip pm-chip--multi" :class="{'pm-chip--set': tags.length}">
        <multiselect
            v-model="tags"
            :options="tagOptions"
            :multiple="true"
            track-by="id"
            label="name_translated"
            :placeholder="$t('processes.create.tags_label')"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            @input="reload"
            qid="pm-filter-tags"
        ></multiselect>
      </div>
      <span class="grow"></span>
      <div class="pm-search">
        <font-awesome-icon icon="search"/>
        <input type="text"
               v-model="keyword"
               @input="debouncedReload"
               :placeholder="$t('projects.search_placeholder')"
               qid="pm-search">
      </div>
    </div>

    <div class="pm-card">
      <table class="pm-table">
        <thead>
          <tr>
            <th style="width:32%"
                class="pm-sortable"
                @click="toggleSort"
                qid="pm-sort-title">
              {{ $t('projects.management.columns.project') }}
              <font-awesome-icon :icon="order === 'asc' ? 'caret-up' : 'caret-down'" class="ml-1"/>
            </th>
            <th>{{ $t('projects.management.columns.type') }}</th>
            <th>{{ $t('projects.management.columns.scope') }}</th>
            <th>{{ $t('projects.management.columns.schedule') }}</th>
            <th>{{ $t('projects.management.columns.progress') }}</th>
            <th>{{ $t('projects.management.columns.team') }}</th>
            <th>{{ $t('projects.management.columns.status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading"><td colspan="8" class="text-muted"><font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('projects.management.loading') }}</td></tr>
          <tr v-if="!loading && !recurring.length && !oneTime.length"><td colspan="8" class="text-muted">{{ $t('projects.management.no_projects') }}</td></tr>

          <tr class="pm-table__group" v-if="!loading && recurring.length">
            <td colspan="8">{{ $t('projects.management.recurring') }} · {{ recurring.length }}</td>
          </tr>
          <tr v-for="project in loading ? [] : recurring" :key="project.slug" qid="pm-recurring-row">
            <td><ProjectName :project="project"/></td>
            <td><span class="pm-tag" :class="'pm-tag--' + meta(project.type).color">{{ $t('projects.management.types.' + project.type) }}</span></td>
            <td>{{ project.scope ? $t('projects.launcher.scopes.' + project.scope) : '—' }}</td>
            <td><span class="pm-rec"><font-awesome-icon icon="repeat"/>{{ scheduleText(project) }}</span></td>
            <td>
              <span class="pm-tag pm-tag--amber" v-if="project.is_active_child_project">{{ $t('projects.launcher.running_now') }}</span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td><Team :users="project.users"/></td>
            <td>
              <span class="pm-tag pm-tag--red" v-if="project.run_due">{{ $t('projects.launcher.due_now') }}</span>
              <span class="pm-tag pm-tag--grey" v-else-if="project.scheduled">{{ $t('projects.management.scheduled') }}</span>
              <span class="pm-tag pm-tag--grey" v-else>{{ $t('projects.launcher.schedule.none') }}</span>
            </td>
            <td class="text-right"><RowActions :project="project" @delete="confirmDelete"/></td>
          </tr>

          <tr class="pm-table__group" v-if="!loading && oneTime.length">
            <td colspan="8">{{ $t('projects.management.one_time') }} · {{ oneTimeTotal }}</td>
          </tr>
          <tr v-for="project in loading ? [] : oneTime" :key="project.slug" qid="pm-one-time-row">
            <td><ProjectName :project="project"/></td>
            <td><span class="pm-tag" :class="'pm-tag--' + meta(project.type).color">{{ $t('projects.management.types.' + project.type) }}</span></td>
            <td>{{ project.scope ? $t('projects.launcher.scopes.' + project.scope) : '—' }}</td>
            <td class="pm-due">{{ periodText(project) }}</td>
            <td><ProgressBar :value="project.progress" :tone="isOverdue(project) ? 'red' : ''"/></td>
            <td><Team :users="project.users"/></td>
            <td><span class="pm-tag" :class="'pm-tag--' + statusTone(project)">{{ statusText(project) }}</span></td>
            <td class="text-right"><RowActions :project="project" @delete="confirmDelete"/></td>
          </tr>
        </tbody>
      </table>
      <div class="pm-card__foot" v-if="oneTimeTotal > perPage">
        <b-pagination
            v-model="page"
            :total-rows="oneTimeTotal"
            :per-page="perPage"
            size="sm"
            class="mb-0 ml-auto"
            @input="loadOneTime"
        ></b-pagination>
      </div>
    </div>

    <b-modal ref="delete-project" hide-footer>
      <div slot="modal-title">
        <h4 qid="delete-s-title">{{ $t('activities.delete.title') }}</h4>
      </div>
      <h5>{{ $t('activities.delete.description') }} {{ toDelete.title }}</h5>
      <h5>{{ $t('activities.delete.are_you_sure') }}</h5>
      <div class="text-right">
        <b-button variant="secondary" @click="$refs['delete-project'].hide()" qid="delete-s-cancel-button">{{ $t('activities.delete.cancel') }}</b-button>
        <b-button class="ml-3"
                  variant="danger"
                  @click="deleteProject"
                  qid="delete-s-submit-button">{{ $t('activities.delete.submit') }}</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import { typeMeta, allowedFamilies, familyTypes, nextRunDate } from '@/views/Projects/Management/projectTypes.js'
  import ProgressBar from '@/views/Projects/Management/ProgressBar.vue';
  import Team from '@/views/Projects/Management/Team.vue';

  const ProjectName = {
    name: 'ProjectName',
    props: ['project'],
    render(h) {
      let project = this.project;
      let meta = typeMeta(project.type);
      let context = [];
      if (project.project && project.project.slug) {
        context.push(this.$t('projects.management.round_of') + ' ' + project.project.title);
      }
      return h('div', {class: 'pm-name'}, [
        h('span', {class: ['pm-icon', 'pm-c-' + meta.color]}, [h('font-awesome-icon', {props: {icon: meta.icon}})]),
        h('div', {class: 'grow'}, [
          h('router-link', {class: 'pm-name__title', props: {to: {name: 'projects.view', params: {slug: project.slug}}}}, project.title),
          context.length ? h('div', {class: 'pm-name__ctx'}, context.join(' · ')) : null
        ])
      ]);
    }
  };

  const RowActions = {
    name: 'RowActions',
    props: ['project'],
    render(h) {
      let project = this.project;
      if (!this.isGranted([this.permissions.PROJECT_DELETE, this.permissions.PROJECT_DELETE_OWN], project) || project.is_active_child_project) {
        return h('span');
      }
      return h('span', {
        class: 'pm-row-action',
        attrs: {qid: 'delete-s-modal', title: this.$t('activities.delete.title')},
        on: {click: () => this.$emit('delete', project)}
      }, [h('font-awesome-icon', {props: {icon: 'trash-alt'}})]);
    }
  };

  // All projects of the Project management page; replaces the asset, control and assessment project lists (US 9396)
  export default {
    name: 'AllProjects',
    components: {
      ProgressBar,
      Team,
      ProjectName,
      RowActions
    },
    data: function() {
      return {
        loading: true,
        recurring: [],
        oneTime: [],
        oneTimeTotal: 0,
        counts: {},
        page: 1,
        perPage: 25,
        order: 'asc',
        status: 'open',
        schedule: '',
        scope: '',
        owners: [],
        tags: [],
        keyword: '',
        users: [],
        tagOptions: [],
        toDelete: {},
        debounceTimer: null
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      families() {
        return allowedFamilies(this.getClient);
      },
      family() {
        let family = this.$route.query.family;
        return _.some(this.families, {key: family}) ? family : '';
      },
      scopeOptions() {
        let client = this.getClient || {};
        let options = [];
        if (client.processingActivitiesModule) {
          options.push('process', 'activity');
        }
        if (client.systemModule) {
          options.push('system');
        }
        if (client.contractBasicModule) {
          options.push('third_party');
        }
        if (client.policyModule) {
          options.push('policy');
        }
        return options;
      }
    },
    watch: {
      family: function() {
        this.reload();
      }
    },
    methods: {
      meta(type) {
        return typeMeta(type);
      },
      setFamily(key) {
        let query = Object.assign({}, this.$route.query, {view: 'list', family: key || undefined});
        this.$router.replace({path: '/projects', query: query});
      },
      toggleSort() {
        this.order = this.order === 'asc' ? 'desc' : 'asc';
        this.reload();
      },
      debouncedReload() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(this.reload, 350);
      },
      // The filters every request shares; the family is added by the caller
      filterParams() {
        let params = {
          client: this.getClient.slug,
          without_parent: 1,
          keyword: this.keyword || undefined,
          scope: this.scope || undefined,
          owners: this.owners.length ? _.map(this.owners, 'id') : undefined,
          tags: this.tags.length ? _.map(this.tags, 'id') : undefined
        };
        if (this.status === 'open') {
          params.open = 1;
        } else if (this.status === 'done') {
          params.statuses = ['completed', 'closed'];
        }
        return params;
      },
      typesParam(family) {
        if (family) {
          return familyTypes(family);
        }
        return _.flatMap(this.families, 'types');
      },
      reload() {
        this.page = 1;
        this.loading = true;
        Promise.all([this.loadRecurring(), this.loadOneTime()]).then(() => {
          this.loading = false;
        }, () => {
          this.loading = false;
        });
        this.loadCounts();
      },
      loadRecurring() {
        if (this.schedule === 'one time') {
          this.recurring = [];
          return Promise.resolve();
        }
        let params = Object.assign(this.filterParams(), {control_type: 'continuous', types: this.typesParam(this.family), per_page: 100, sort: 'title', order: this.order});
        return piincHttp.get('projects', {params: params}).then((response) => {
          this.recurring = response.data.items;
        });
      },
      loadOneTime() {
        if (this.schedule === 'continuous') {
          this.oneTime = [];
          this.oneTimeTotal = 0;
          return Promise.resolve();
        }
        let params = Object.assign(this.filterParams(), {control_type: 'one time', types: this.typesParam(this.family), page: this.page, per_page: this.perPage, sort: 'title', order: this.order});
        return piincHttp.get('projects', {params: params}).then((response) => {
          this.oneTime = response.data.items;
          this.oneTimeTotal = response.data.total_count;
        });
      },
      // Counts on the family tabs, with the other filters applied
      loadCounts() {
        let keys = [''].concat(_.map(this.families, 'key'));
        let base = this.filterParams();
        if (this.schedule) {
          base.control_type = this.schedule;
        }
        _.forEach(keys, (key) => {
          piincHttp.get('projects', {params: Object.assign({}, base, {types: this.typesParam(key), per_page: 1})}).then((response) => {
            this.$set(this.counts, key || 'all', response.data.total_count);
          }, () => {});
        });
      },
      scheduleText(project) {
        let next = nextRunDate(project);
        if (!project.scheduled || !next) {
          return this.$t('projects.launcher.schedule.none');
        }
        let every = project.repeat_after + ' ' + this.$t('system.interval_' + (project.repeat_after_interval || '').toLowerCase()).toLowerCase();
        return this.$t('projects.launcher.every') + ' ' + every + ' · ' +
          (project.auto_create ? this.$t('projects.management.auto') : this.$t('projects.management.manual')).toLowerCase() + ' · ' +
          next.format('D MMM YYYY');
      },
      periodText(project) {
        var moment = require('moment');
        if (!project.start_date) {
          return '—';
        }
        return moment(project.start_date).format('D MMM') + (project.end_date ? ' → ' + moment(project.end_date).format('D MMM YYYY') : '');
      },
      isOverdue(project) {
        var moment = require('moment');
        return project.status === 'in progress' && !!project.end_date && moment(project.end_date).endOf('day').isBefore(moment());
      },
      statusText(project) {
        if (this.isOverdue(project)) {
          return this.$t('projects.management.overdue');
        }
        let labels = {
          'pending': 'system.pending',
          'in progress': 'system.in_progress',
          'completed': 'projects.button.completed_label',
          'closed': 'projects.button.closed_label'
        };
        return labels[project.status] ? this.$t(labels[project.status]) : '—';
      },
      statusTone(project) {
        if (this.isOverdue(project)) {
          return 'red';
        }
        return {'in progress': 'teal', 'pending': 'amber'}[project.status] || 'grey';
      },
      confirmDelete(project) {
        this.toDelete = project;
        this.$refs['delete-project'].show();
      },
      deleteProject() {
        piincHttp.delete('projects/' + this.toDelete.slug).then(() => {
          this.$refs['delete-project'].hide();
          this.$toastr('success', this.$t('projects.delete.success'));
          this.reload();
        }, () => {});
      },
      loadUsers(query) {
        piincHttp.get('users', {params: {client: this.getClient.id, keyword: query || undefined, per_page: 50}}).then((response) => {
          this.users = response.data.items;
        }, () => {});
      },
      loadTags() {
        piincHttp.get('tags', {params: {client: this.getClient.slug}}).then((response) => {
          this.tagOptions = response.data;
        }, () => {});
      }
    },
    created() {
      if (this.getClient && this.getClient.slug) {
        this.reload();
        this.loadUsers();
        this.loadTags();
      }
    }
  }
</script>
